import { Injectable } from '@angular/core';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { Observable } from 'rxjs';
import { filter, map, withLatestFrom, mapTo, audit } from 'rxjs/operators';
import { PatientQuery } from '../state/patient-query';
import { combineQueries } from '@datorama/akita';
import { DateOfBirthUtils } from '@shared/utils/date-of-birth-utils';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { PatientAppIframeId } from '@shared/models/enums/patient-app-iframe-id.enum';
import { IntuitiveFlowService } from '@shared/services/intuitive-flow.service';

@Injectable({
	providedIn: 'root'
})
export class PatientAppUrlBuilderService {
	private readonly apiEndpointDependingOnHost$ = this.shellQuery.apiEndpoint$.pipe(
		withLatestFrom(this.shellQuery.patientAppApiEndpoint$, this.hostPlatformService.isScanner$),
		map(([apiEndpoint, patientAppApiEndpoint, isHostPlatformScanner]) => {
			const finalApiEndpoint = patientAppApiEndpoint ?? apiEndpoint;

			return isHostPlatformScanner ? finalApiEndpoint?.replace('RxEditor', '') : `${finalApiEndpoint}/mvc`;
		})
	);

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly patientQuery: PatientQuery,
		private readonly hostPlatformService: HostPlatformService,
		private readonly intuitiveFlowService: IntuitiveFlowService
	) {}

	getInlinePatientAppUrl(): Observable<string> {
		const allConfigLoaded = combineQueries([this.shellQuery.userSettings$, this.shellQuery.rxConfiguration$]).pipe(
			filter(([userSettings, rxConfiguration]) => !!(userSettings && rxConfiguration)),
			mapTo(true)
		);

		return combineQueries([
			this.shellQuery.patientAppUrl$,
			this.shellQuery.companyId$,
			this.shellQuery.contactId$,
			this.shellQuery.dateFormat$,
			this.apiEndpointDependingOnHost$,
			this.shellQuery.languageCode$,
			this.shellQuery.regulatoryDOBMask$,
			this.intuitiveFlowService.isIntuitiveFlow$
		]).pipe(
			audit(() => allConfigLoaded),
			filter(([patientAppUrl]) => !!patientAppUrl),
			map(
				([
					patientAppUrl,
					companyId,
					contactId,
					dateFormat,
					patientAppApiEndpoint,
					languageCode,
					dateOfBirthMask,
					isIntuitiveFlow
				]) => {
					const params = {
						baseUrl: patientAppUrl as string,
						companyId: companyId as number,
						contactId: contactId as number,
						dateOfBirthFormat: DateOfBirthUtils.getFormat(dateFormat, dateOfBirthMask),
						patientAppApiEndpoint: patientAppApiEndpoint as string,
						languageCode,
						isIntuitiveFlow
					};

					return this.buildInlineUrl(params);
				}
			)
		);
	}

	getEditPatientAppUrl(isInlineEdit: boolean = false, isInitialConfirmAllowed: boolean = true): Observable<string> {
		return combineQueries([
			this.shellQuery.patientAppUrl$,
			this.shellQuery.companyId$,
			this.apiEndpointDependingOnHost$,
			this.shellQuery.languageCode$,
			this.hostPlatformService.isScanner$,
			this.shellQuery.dateFormat$,
			this.shellQuery.regulatoryDOBMask$,
			this.intuitiveFlowService.isIntuitiveFlow$,
			this.shellQuery.isRxTakenForScan$
		]).pipe(
			filter(([patientAppUrl]) => !!patientAppUrl),
			withLatestFrom(this.patientQuery.patient$),
			map(
				([
					[
						patientAppUrl,
						companyId,
						patientAppApiEndpoint,
						languageCode,
						isScanner,
						dateFormat,
						dateOfBirthMask,
						isIntuitiveFlow,
						isRxTakenForScan
					],
					patient
				]) =>
					this.buildEditUrl({
						baseUrl: patientAppUrl as string,
						companyId: companyId as number,
						patientAppApiEndpoint: patientAppApiEndpoint as string,
						languageCode,
						patientUid: patient?.UID as string,
						patientType: patient?.Type as number,
						isMidcSource: !isScanner,
						dateOfBirthFormat: DateOfBirthUtils.getFormat(dateFormat, dateOfBirthMask),
						isInlineEdit,
						isIntuitiveFlow,
						isInitialConfirmAllowed,
						isRxTakenForScan: !!isRxTakenForScan
					})
			)
		);
	}

	getSearchPatientAppUrl(): Observable<string> {
		return combineQueries([
			this.shellQuery.patientAppUrl$,
			this.shellQuery.companyId$,
			this.apiEndpointDependingOnHost$,
			this.shellQuery.languageCode$,
			this.shellQuery.dateFormat$,
			this.shellQuery.regulatoryDOBMask$
		]).pipe(
			filter(([patientAppUrl]) => !!patientAppUrl),
			map(([patientAppUrl, companyId, patientAppApiEndpoint, languageCode, dateFormat, dateOfBirthMask]) =>
				this.buildSearchUrl({
					baseUrl: patientAppUrl as string,
					companyId: companyId as number,
					patientAppApiEndpoint: patientAppApiEndpoint as string,
					languageCode,
					dateOfBirthFormat: DateOfBirthUtils.getFormat(dateFormat, dateOfBirthMask)
				})
			)
		);
	}

	private buildInlineUrl({
		baseUrl,
		companyId,
		contactId,
		patientAppApiEndpoint,
		dateOfBirthFormat,
		languageCode,
		isIntuitiveFlow
	}: {
		baseUrl: string;
		companyId: number;
		contactId: number;
		patientAppApiEndpoint: string;
		dateOfBirthFormat: string;
		languageCode: string;
		isIntuitiveFlow: boolean;
	}): string {
		const queryString = new URLSearchParams({
			isInlineMode: `${true}`,
			companyid: `${companyId}`,
			doctorid: `${contactId}`,
			endpoint: `${patientAppApiEndpoint}`,
			internalEventId: `${28}`,
			dateOfBirthFormat: `${dateOfBirthFormat}`,
			'lang-Code': `${languageCode}`,
			isIntuitiveFlow: `${isIntuitiveFlow}`,
			iframeId: PatientAppIframeId.Inline
		});

		return `${baseUrl}?${queryString}`;
	}

	private buildEditUrl({
		baseUrl,
		patientUid,
		companyId,
		patientType,
		patientAppApiEndpoint,
		languageCode,
		isMidcSource,
		dateOfBirthFormat,
		isInlineEdit,
		isIntuitiveFlow,
		isInitialConfirmAllowed,
		isRxTakenForScan
	}: {
		baseUrl: string;
		patientUid: string;
		companyId: number;
		patientType: number;
		patientAppApiEndpoint: string;
		languageCode: string;
		isMidcSource: boolean;
		dateOfBirthFormat: string;
		isInlineEdit: boolean;
		isIntuitiveFlow: boolean;
		isInitialConfirmAllowed: boolean;
		isRxTakenForScan: boolean;
	}): string {
		const queryString = new URLSearchParams({
			id: patientUid,
			companyid: `${companyId}`,
			type: `${patientType}`,
			endpoint: `${patientAppApiEndpoint}`,
			internalEventId: `${28}`,
			isMidcSource: `${isMidcSource}`,
			'lang-Code': `${languageCode}`,
			dateOfBirthFormat: `${dateOfBirthFormat}`,
			iframeId: PatientAppIframeId.Edit,
			isInlineEdit: `${isInlineEdit}`,
			isIntuitiveFlow: `${isIntuitiveFlow}`,
			isInitialConfirmAllowed: `${isInitialConfirmAllowed}`,
			isRxTakenForScan: `${isRxTakenForScan}`
		});

		return `${baseUrl}/patient?${queryString}`;
	}

	private buildSearchUrl({
		baseUrl,
		companyId,
		patientAppApiEndpoint,
		languageCode,
		dateOfBirthFormat
	}: {
		baseUrl: string;
		companyId: number;
		patientAppApiEndpoint: string;
		languageCode: string;
		dateOfBirthFormat: string;
	}): string {
		const queryString = new URLSearchParams({
			companyid: `${companyId}`,
			endpoint: `${patientAppApiEndpoint}`,
			'lang-Code': `${languageCode}`,
			dateOfBirthFormat: `${dateOfBirthFormat}`,
			iframeId: PatientAppIframeId.Search
		});

		return `${baseUrl}/patient-search?${queryString}`;
	}
}
